import { useState, useEffect, useRef } from 'react';
import { Sword, Skull, Target, Zap, Heart, Clock, Shield, Snowflake, Star, Crosshair } from 'lucide-react';
import PowerUpGlossary from './components/PowerUpGlossary';
import StartScreen from './components/StartScreen';
import GameOverScreen from './components/GameOverScreen';
import PowerUpDisplay from './components/PowerUpDisplay';
import { PowerUpType, WeaponType } from './types';

const ZombieSwordsmanGame = () => {
  // Game state
  const [gameStarted, setGameStarted] = useState(false);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [hasGun, setHasGun] = useState(false);
  const [hasAxe, setHasAxe] = useState(false);
  const [hasLaser, setHasLaser] = useState(false);
  const [hasGrenade, setHasGrenade] = useState(false);
  const [currentWeapon, setCurrentWeapon] = useState<WeaponType>('sword');
  const [isLasering, setIsLasering] = useState(false);
  const [grenades, setGrenades] = useState<Array<{id: number, x: number, y: number, targetX: number, targetY: number, startTime: number, duration: number}>>([]);
  const [health, setHealth] = useState(3);
  const [powerUps, setPowerUps] = useState<Array<{id: number, x: number, y: number, type: PowerUpType}>>([]);
  const [activePowerUps, setActivePowerUps] = useState<{
    speed: boolean,
    slowZombies: boolean,
    doubleDamage: boolean,
    shield: boolean,
    rapidFire: boolean,
    freezeZombies: boolean,
    scoreMultiplier: boolean
  }>({
    speed: false,
    slowZombies: false,
    doubleDamage: false,
    shield: false,
    rapidFire: false,
    freezeZombies: false,
    scoreMultiplier: false
  });
  const swordsmanPos = useRef({ x: 0, y: 0 });
  const [zombies, setZombies] = useState<Array<{id: number, x: number, y: number, health: number}>>([]);
  const [bullets, setBullets] = useState<Array<{id: number, x: number, y: number, angle: number}>>([]);
  const animationState = useRef({ moving: false, attacking: false, shooting: false, spinning: false, lasering: false });
  const gameAreaRef = useRef<HTMLDivElement>(null);
  const attackCooldownRef = useRef(false);
  const shootCooldownRef = useRef(false);
  const mousePosRef = useRef({ x: 0, y: 0 });
  const keysPressedRef = useRef({ w: false, a: false, s: false, d: false });
  const animationFrameRef = useRef<number>();
  const lastUpdateTimeRef = useRef(0);
  const zombieSpawnTimerRef = useRef<NodeJS.Timeout>();
  const powerUpTimersRef = useRef<{[key: string]: NodeJS.Timeout}>({});
  const [powerUpTimeLeft, setPowerUpTimeLeft] = useState<{[key: string]: number}>({});
  const baseMoveSpeed = 3;
  const currentMoveSpeed = useRef(baseMoveSpeed);

  // Initialize game
  useEffect(() => {
    if (gameAreaRef.current) {
      const { width, height } = gameAreaRef.current.getBoundingClientRect();
      swordsmanPos.current = { x: width / 2, y: height / 2 };
      if (gameStarted) {
        startZombieSpawning();
      }
    }

    // Event listeners
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (['w', 'a', 's', 'd'].includes(key)) {
        keysPressedRef.current = { ...keysPressedRef.current, [key]: true };
        animationState.current.moving = true;
      }

      // Attack with F key
      if (key === 'f') {
        handleAttack();
      }

      // Weapon switching
      if (key === '1') {
        setCurrentWeapon('sword');
      } else if (key === '2' && hasGun) {
        setCurrentWeapon('gun');
      } else if (key === '3' && hasAxe) {
        setCurrentWeapon('axe');
      } else if (key === '4' && hasLaser) {
        setCurrentWeapon('laser');
      } else if (key === '5' && hasGrenade) {
        setCurrentWeapon('grenade');
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (['w', 'a', 's', 'd'].includes(key)) {
        keysPressedRef.current = { ...keysPressedRef.current, [key]: false };

        if (!keysPressedRef.current.w && !keysPressedRef.current.a &&
            !keysPressedRef.current.s && !keysPressedRef.current.d) {
          animationState.current.moving = false;
        }
      }

      // Stop laser when F key is released
      if (key === 'f' && currentWeapon === 'laser') {
        stopLaser();
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!gameAreaRef.current) return;
      const rect = gameAreaRef.current.getBoundingClientRect();
      mousePosRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    };

    const handleAttack = () => {
      if (currentWeapon === 'gun') {
        shootBullet();
      } else if (currentWeapon === 'axe') {
        spinAttack();
      } else if (currentWeapon === 'laser') {
        startLaser();
      } else if (currentWeapon === 'grenade') {
        throwGrenade();
      } else {
        attackZombies();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('contextmenu', (e) => e.preventDefault());

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameRef.current!);
      clearTimeout(zombieSpawnTimerRef.current!);
      Object.values(powerUpTimersRef.current).forEach(timer => clearTimeout(timer));
    };
  }, [hasGun, hasAxe, hasLaser, hasGrenade, currentWeapon, gameStarted]);

  // Zombie spawning function
  const startZombieSpawning = () => {
    const spawnZombie = () => {
      if (gameOver || !gameAreaRef.current) return;
      
      const { width, height } = gameAreaRef.current.getBoundingClientRect();
      const side = Math.floor(Math.random() * 4);
      let x, y;
      
      switch (side) {
        case 0: x = Math.random() * width; y = -30; break;
        case 1: x = width + 30; y = Math.random() * height; break;
        case 2: x = Math.random() * width; y = height + 30; break;
        case 3: x = -30; y = Math.random() * height; break;
        default: x = 0; y = 0;
      }
      
      setZombies(prev => [...prev, { 
        id: Date.now() + Math.random(),
        x, 
        y, 
        health: 1 
      }]);
      
      zombieSpawnTimerRef.current = setTimeout(
        spawnZombie,
        activePowerUps.slowZombies ? 2000 :
        (currentWeapon === 'gun' || currentWeapon === 'axe' || currentWeapon === 'laser' || currentWeapon === 'grenade') ? 1000 : 1500
      );
    };
    
    zombieSpawnTimerRef.current = setTimeout(spawnZombie, 1000);
  };

  // Generate random powerup when zombie dies
  const maybeSpawnPowerUp = (x: number, y: number) => {
    // Base chance starts low and increases with score
    let powerUpChance = 0.08 + (score / 10000); // 8% base, increases with score
    powerUpChance = Math.min(powerUpChance, 0.25); // Cap at 25%

    // Add randomization - sometimes no drops for a while, then clusters
    const randomFactor = Math.random();
    if (randomFactor < 0.3) powerUpChance *= 0.5; // 30% chance for reduced drops
    else if (randomFactor > 0.9) powerUpChance *= 2; // 10% chance for increased drops

    if (Math.random() < powerUpChance) {
      // Weighted power-up distribution (some rarer than others)
      const powerUpPool = [
        'health', 'health', 'health', // Common (3x weight)
        'speed', 'speed', // Uncommon (2x weight)
        'slowZombies', 'doubleDamage', // Uncommon
        'shield', 'rapidFire', // Rare
        'freezeZombies', 'scoreMultiplier' // Very rare
      ];
      const randomType = powerUpPool[Math.floor(Math.random() * powerUpPool.length)] as PowerUpType;
      
      setPowerUps(prev => [...prev, {
        id: Date.now() + Math.random(),
        x,
        y,
        type: randomType
      }]);
    }
  };

  // Helper function to start power-up timer with countdown
  const startPowerUpTimer = (type: PowerUpType, duration: number, onExpire: () => void) => {
    // Clear any existing timer
    if (powerUpTimersRef.current[type]) {
      clearTimeout(powerUpTimersRef.current[type]);
    }

    // Set initial time
    setPowerUpTimeLeft(prev => ({...prev, [type]: duration}));

    // Update countdown every second
    const countdownInterval = setInterval(() => {
      setPowerUpTimeLeft(prev => {
        const newTime = (prev[type] || 0) - 1000;
        if (newTime <= 0) {
          clearInterval(countdownInterval);
          return {...prev, [type]: 0};
        }
        return {...prev, [type]: newTime};
      });
    }, 1000);

    // Set main timer
    powerUpTimersRef.current[type] = setTimeout(() => {
      clearInterval(countdownInterval);
      setPowerUpTimeLeft(prev => ({...prev, [type]: 0}));
      onExpire();
    }, duration);
  };

  // Handle collecting powerup
  const collectPowerUp = (powerUp: {id: number, x: number, y: number, type: PowerUpType}) => {
    setPowerUps(prev => prev.filter(p => p.id !== powerUp.id));

    // Clear any existing timer for this powerup type
    if (powerUpTimersRef.current[powerUp.type]) {
      clearTimeout(powerUpTimersRef.current[powerUp.type]);
    }

    switch (powerUp.type) {
      case 'speed':
        currentMoveSpeed.current = baseMoveSpeed * 1.5;
        setActivePowerUps(prev => ({...prev, speed: true}));
        startPowerUpTimer('speed', 10000, () => {
          currentMoveSpeed.current = baseMoveSpeed;
          setActivePowerUps(prev => ({...prev, speed: false}));
        });
        break;
        
      case 'health':
        setHealth(prev => Math.min(5, prev + 1));
        break;
        
      case 'slowZombies':
        setActivePowerUps(prev => ({...prev, slowZombies: true}));
        // Clear any existing zombie spawn timer
        if (zombieSpawnTimerRef.current) {
          clearTimeout(zombieSpawnTimerRef.current);
        }
        startZombieSpawning(); // Restart spawning with new timing
        startPowerUpTimer('slowZombies', 15000, () => {
          setActivePowerUps(prev => ({...prev, slowZombies: false}));
          if (zombieSpawnTimerRef.current) {
            clearTimeout(zombieSpawnTimerRef.current);
          }
          startZombieSpawning(); // Restart spawning with original timing
        });
        break;
        
      case 'doubleDamage':
        setActivePowerUps(prev => ({...prev, doubleDamage: true}));
        startPowerUpTimer('doubleDamage', 10000, () => {
          setActivePowerUps(prev => ({...prev, doubleDamage: false}));
        });
        break;

      case 'shield':
        setActivePowerUps(prev => ({...prev, shield: true}));
        startPowerUpTimer('shield', 15000, () => {
          setActivePowerUps(prev => ({...prev, shield: false}));
        });
        break;

      case 'rapidFire':
        setActivePowerUps(prev => ({...prev, rapidFire: true}));
        startPowerUpTimer('rapidFire', 12000, () => {
          setActivePowerUps(prev => ({...prev, rapidFire: false}));
        });
        break;

      case 'freezeZombies':
        setActivePowerUps(prev => ({...prev, freezeZombies: true}));
        startPowerUpTimer('freezeZombies', 8000, () => {
          setActivePowerUps(prev => ({...prev, freezeZombies: false}));
        });
        break;

      case 'scoreMultiplier':
        setActivePowerUps(prev => ({...prev, scoreMultiplier: true}));
        startPowerUpTimer('scoreMultiplier', 20000, () => {
          setActivePowerUps(prev => ({...prev, scoreMultiplier: false}));
        });
        break;
    }
  };

  // Attack logic (sword)
  const attackZombies = () => {
    if (attackCooldownRef.current) return;
    attackCooldownRef.current = true;
    animationState.current.attacking = true;
    
    setZombies(prevZombies => {
      const newZombies = prevZombies.filter(zombie => {
        const distance = Math.sqrt(
          Math.pow(zombie.x - swordsmanPos.current.x, 2) + 
          Math.pow(zombie.y - swordsmanPos.current.y, 2)
        );
        if (distance < 60) {
          let points = activePowerUps.doubleDamage ? 20 : 10;
          if (activePowerUps.scoreMultiplier) points *= 2;
          setScore(s => s + points);
          maybeSpawnPowerUp(zombie.x, zombie.y);
          return false; // Remove zombie
        }
        return true; // Keep zombie
      });
      return newZombies;
    });
    
    setTimeout(() => {
      attackCooldownRef.current = false;
      animationState.current.attacking = false;
    }, 300);
  };

  // Spin attack logic (axe)
  const spinAttack = () => {
    if (attackCooldownRef.current) return;
    attackCooldownRef.current = true;
    animationState.current.spinning = true;

    setZombies(prevZombies => {
      const newZombies = prevZombies.filter(zombie => {
        const distance = Math.sqrt(
          Math.pow(zombie.x - swordsmanPos.current.x, 2) +
          Math.pow(zombie.y - swordsmanPos.current.y, 2)
        );
        if (distance < 120) { // Larger range than sword
          let points = activePowerUps.doubleDamage ? 25 : 15;
          if (activePowerUps.scoreMultiplier) points *= 2;
          setScore(s => s + points);
          maybeSpawnPowerUp(zombie.x, zombie.y);
          return false; // Remove zombie
        }
        return true; // Keep zombie
      });
      return newZombies;
    });

    setTimeout(() => {
      attackCooldownRef.current = false;
      animationState.current.spinning = false;
    }, 500); // Longer cooldown for powerful attack
  };

  // Laser attack logic
  const startLaser = () => {
    if (currentWeapon !== 'laser') return;
    setIsLasering(true);
    animationState.current.lasering = true;
  };

  const stopLaser = () => {
    setIsLasering(false);
    animationState.current.lasering = false;
  };

  // Grenade launcher logic
  const throwGrenade = () => {
    if (currentWeapon !== 'grenade' || !gameAreaRef.current) return;

    const targetX = mousePosRef.current.x;
    const targetY = mousePosRef.current.y;
    const startTime = Date.now();
    const duration = 1000; // 1 second flight time

    setGrenades(prev => [...prev, {
      id: Date.now() + Math.random(),
      x: swordsmanPos.current.x,
      y: swordsmanPos.current.y,
      targetX,
      targetY,
      startTime,
      duration
    }]);
  };

  // Shooting logic (gun)
  const shootBullet = () => {
    if (shootCooldownRef.current || currentWeapon !== 'gun' || !gameAreaRef.current) return;
    shootCooldownRef.current = true;
    animationState.current.shooting = true;
    
    const dx = mousePosRef.current.x - swordsmanPos.current.x;
    const dy = mousePosRef.current.y - swordsmanPos.current.y;
    const angle = Math.atan2(dy, dx);
    
    setBullets(prev => [...prev, {
      id: Date.now(),
      x: swordsmanPos.current.x,
      y: swordsmanPos.current.y,
      angle
    }]);
    
    let cooldownTime = activePowerUps.doubleDamage ? 150 : 300;
    if (activePowerUps.rapidFire) cooldownTime *= 0.3;

    setTimeout(() => {
      shootCooldownRef.current = false;
      animationState.current.shooting = false;
    }, cooldownTime);
  };

  // Check for weapon upgrades
  useEffect(() => {
    if (score >= 500 && !hasGun) {
      setHasGun(true);
      setCurrentWeapon('gun');
    }
    if (score >= 1000 && !hasAxe) {
      setHasAxe(true);
      setCurrentWeapon('axe');
    }
    if (score >= 2000 && !hasLaser) {
      setHasLaser(true);
      setCurrentWeapon('laser');
    }
    if (score >= 5000 && !hasGrenade) {
      setHasGrenade(true);
      setCurrentWeapon('grenade');
    }
  }, [score, hasGun, hasAxe, hasLaser, hasGrenade]);

  // Game loop
  useEffect(() => {
    const gameLoop = (timestamp: number) => {
      const deltaTime = timestamp - lastUpdateTimeRef.current;
      lastUpdateTimeRef.current = timestamp;

      if (deltaTime > 1000 / 60) {
        // Move player with WASD
        if (gameAreaRef.current && !gameOver) {
          const { width, height } = gameAreaRef.current.getBoundingClientRect();
          const prevX = swordsmanPos.current.x;
          const prevY = swordsmanPos.current.y;
          
          let newX = prevX;
          let newY = prevY;
          
          if (keysPressedRef.current.w) newY = Math.max(0, newY - currentMoveSpeed.current);
          if (keysPressedRef.current.s) newY = Math.min(height, newY + currentMoveSpeed.current);
          if (keysPressedRef.current.a) newX = Math.max(0, newX - currentMoveSpeed.current);
          if (keysPressedRef.current.d) newX = Math.min(width, newX + currentMoveSpeed.current);
          
          swordsmanPos.current = { x: newX, y: newY };
        }

        // Move bullets
        setBullets(prev => prev.map(bullet => {
          const speed = 10;
          return {
            ...bullet,
            x: bullet.x + Math.cos(bullet.angle) * speed,
            y: bullet.y + Math.sin(bullet.angle) * speed
          };
        }).filter(bullet => {
          if (!gameAreaRef.current) return false;
          const { width, height } = gameAreaRef.current.getBoundingClientRect();
          return bullet.x > 0 && bullet.x < width && bullet.y > 0 && bullet.y < height;
        }));

        // Move grenades and handle explosions
        setGrenades(prev => prev.filter(grenade => {
          const currentTime = Date.now();
          const elapsed = currentTime - grenade.startTime;
          const progress = Math.min(elapsed / grenade.duration, 1);

          if (progress >= 1) {
            // Grenade exploded - damage zombies in explosion radius
            setZombies(prevZombies => {
              return prevZombies.filter(zombie => {
                const distance = Math.sqrt(
                  Math.pow(zombie.x - grenade.targetX, 2) +
                  Math.pow(zombie.y - grenade.targetY, 2)
                );
                if (distance < 80) { // Explosion radius
                  let points = activePowerUps.doubleDamage ? 50 : 25;
                  if (activePowerUps.scoreMultiplier) points *= 2;
                  setScore(s => s + points);
                  maybeSpawnPowerUp(zombie.x, zombie.y);
                  return false; // Remove zombie
                }
                return true; // Keep zombie
              });
            });
            return false; // Remove grenade
          }
          return true; // Keep grenade
        }));

        // Move zombies
        setZombies(prevZombies => {
          let zombieSpeed = (currentWeapon === 'gun' || currentWeapon === 'axe' || currentWeapon === 'laser' || currentWeapon === 'grenade') ? 2 : 1.5;
          if (activePowerUps.slowZombies) zombieSpeed *= 0.5;
          if (activePowerUps.freezeZombies) zombieSpeed *= 0.1;

          return prevZombies.map(zombie => {
            const dx = swordsmanPos.current.x - zombie.x;
            const dy = swordsmanPos.current.y - zombie.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            return {
              ...zombie,
              x: zombie.x + (dx / distance) * zombieSpeed,
              y: zombie.y + (dy / distance) * zombieSpeed
            };
          }).filter(zombie => {
            const distance = Math.sqrt(
              Math.pow(zombie.x - swordsmanPos.current.x, 2) + 
              Math.pow(zombie.y - swordsmanPos.current.y, 2)
            );
            if (distance < 30 && !gameOver) {
              // Shield power-up reduces damage chance
              const damageChance = activePowerUps.shield ? 0.3 : 1.0;
              if (Math.random() < damageChance) {
                setHealth(prev => {
                  const newHealth = prev - 1;
                  if (newHealth <= 0) {
                    setGameOver(true);
                  }
                  return newHealth;
                });
              }
            }
            return true;
          });
        });

        // Check bullet-zombie collisions
        setBullets(prevBullets => {
          const newBullets = prevBullets.filter(bullet => {
            let hit = false;
            setZombies(prevZombies => {
              return prevZombies.filter(zombie => {
                const distance = Math.sqrt(
                  Math.pow(bullet.x - zombie.x, 2) +
                  Math.pow(bullet.y - zombie.y, 2)
                );
                if (distance < 20) {
                  hit = true;
                  let points = activePowerUps.doubleDamage ? 30 : 15;
                  if (activePowerUps.scoreMultiplier) points *= 2;
                  setScore(s => s + points);
                  maybeSpawnPowerUp(zombie.x, zombie.y);
                  return false;
                }
                return true;
              });
            });
            return !hit;
          });
          return newBullets;
        });

        // Check laser-zombie collisions
        if (isLasering && currentWeapon === 'laser') {
          const laserAngle = Math.atan2(
            mousePosRef.current.y - swordsmanPos.current.y,
            mousePosRef.current.x - swordsmanPos.current.x
          );

          setZombies(prevZombies => {
            return prevZombies.filter(zombie => {
              // Calculate if zombie is in laser path
              const zombieAngle = Math.atan2(
                zombie.y - swordsmanPos.current.y,
                zombie.x - swordsmanPos.current.x
              );
              const angleDiff = Math.abs(zombieAngle - laserAngle);
              const normalizedAngleDiff = Math.min(angleDiff, 2 * Math.PI - angleDiff);

              const distanceToPlayer = Math.sqrt(
                Math.pow(zombie.x - swordsmanPos.current.x, 2) +
                Math.pow(zombie.y - swordsmanPos.current.y, 2)
              );

              // Laser hits if zombie is within 0.1 radians of laser direction and within range
              if (normalizedAngleDiff < 0.1 && distanceToPlayer < 300) {
                let points = activePowerUps.doubleDamage ? 40 : 20;
                if (activePowerUps.scoreMultiplier) points *= 2;
                setScore(s => s + points);
                maybeSpawnPowerUp(zombie.x, zombie.y);
                return false; // Remove zombie
              }
              return true; // Keep zombie
            });
          });
        }

        // Check powerup collection
        setPowerUps(prevPowerUps => {
          return prevPowerUps.filter(powerUp => {
            const distance = Math.sqrt(
              Math.pow(powerUp.x - swordsmanPos.current.x, 2) + 
              Math.pow(powerUp.y - swordsmanPos.current.y, 2)
            );
            if (distance < 30) {
              collectPowerUp(powerUp);
              return false;
            }
            return true;
          });
        });
      }

      animationFrameRef.current = requestAnimationFrame(gameLoop);
    };

    animationFrameRef.current = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(animationFrameRef.current!);
  }, [gameOver, currentWeapon, activePowerUps, isLasering, grenades]);

  // Calculate angle for gun rotation
  const getGunAngle = () => {
    const dx = mousePosRef.current.x - swordsmanPos.current.x;
    const dy = mousePosRef.current.y - swordsmanPos.current.y;
    return Math.atan2(dy, dx);
  };

  // Powerup icon component
  const PowerUpIcon = ({ type }: { type: PowerUpType }) => {
    switch (type) {
      case 'speed': return <Zap className="w-5 h-5 text-yellow-400" />;
      case 'health': return <Heart className="w-5 h-5 text-red-500" />;
      case 'slowZombies': return <Clock className="w-5 h-5 text-blue-400" />;
      case 'doubleDamage': return <Shield className="w-5 h-5 text-purple-400" />;
      case 'shield': return <Shield className="w-5 h-5 text-cyan-400" />;
      case 'rapidFire': return <Crosshair className="w-5 h-5 text-orange-400" />;
      case 'freezeZombies': return <Snowflake className="w-5 h-5 text-blue-200" />;
      case 'scoreMultiplier': return <Star className="w-5 h-5 text-yellow-300" />;
      default: return null;
    }
  };

  // Start game
  const startGame = () => {
    setGameStarted(true);
    resetGame();
  };

  // Reset game
  const resetGame = () => {
    setScore(0);
    setGameOver(false);
    setHealth(3);
    setZombies([]);
    setBullets([]);
    setGrenades([]);
    setPowerUps([]);
    setHasGun(false);
    setHasAxe(false);
    setHasLaser(false);
    setHasGrenade(false);
    setCurrentWeapon('sword');
    setIsLasering(false);
    setActivePowerUps({
      speed: false,
      slowZombies: false,
      doubleDamage: false,
      shield: false,
      rapidFire: false,
      freezeZombies: false,
      scoreMultiplier: false
    });
    currentMoveSpeed.current = baseMoveSpeed;
    animationState.current = { moving: false, attacking: false, shooting: false, spinning: false, lasering: false };
    keysPressedRef.current = { w: false, a: false, s: false, d: false };

    // Clear all timers
    Object.values(powerUpTimersRef.current).forEach(timer => clearTimeout(timer));
    powerUpTimersRef.current = {};

    if (gameAreaRef.current) {
      const { width, height } = gameAreaRef.current.getBoundingClientRect();
      swordsmanPos.current = {
        x: width / 2,
        y: height / 2
      };
    }
    if (gameStarted) {
      startZombieSpawning();
    }
  };

  return (
    <div className="relative flex flex-col items-center justify-center min-h-screen bg-[#121220] text-white overflow-hidden">
      {!gameStarted ? (
        <StartScreen onStartGame={startGame} />
      ) : (
        <>
          <h1 className="text-4xl font-bold mb-4 text-red-500 font-mono tracking-wider">
            ZOMBIE SLAYER {currentWeapon === 'gun' && "⚔️🔫"} {currentWeapon === 'axe' && "⚔️🪓"} {currentWeapon === 'laser' && "⚔️🔴"} {currentWeapon === 'grenade' && "⚔️💣"}
          </h1>
      
      <div className="relative w-full max-w-4xl h-96 bg-gray-900 rounded-lg border-2 border-gray-700 overflow-hidden"
           ref={gameAreaRef}
           onContextMenu={(e) => e.preventDefault()}>
        
        {/* Swordsman character */}
        <div
          className={`absolute w-16 h-16 transition-transform duration-100 ${
            animationState.current.moving ? 'scale-110' : 'scale-100'
          } ${animationState.current.spinning ? 'animate-spin' : ''}`}
          style={{
            left: `${swordsmanPos.current.x - 32}px`,
            top: `${swordsmanPos.current.y - 32}px`,
            transformOrigin: 'center'
          }}>
          <div className="relative w-full h-full">
            <div className="absolute inset-0 bg-purple-800 rounded-full flex items-center justify-center">
              {currentWeapon === 'sword' && (
                <Sword
                  className={`w-8 h-8 text-yellow-300 transition-transform duration-200 ${
                    animationState.current.attacking ? 'rotate-[30deg]' : 'rotate-[-30deg]'
                  }`}
                />
              )}
              {currentWeapon === 'axe' && (
                <div className="text-2xl">🪓</div>
              )}
              {currentWeapon === 'gun' && (
                <Target
                  className={`w-8 h-8 text-red-400 transition-transform duration-200 ${
                    animationState.current.shooting ? 'scale-125' : 'scale-100'
                  }`}
                  style={{
                    transform: `rotate(${getGunAngle()}rad)`
                  }}
                />
              )}
              {currentWeapon === 'laser' && (
                <div className="text-2xl text-red-500">🔴</div>
              )}
              {currentWeapon === 'grenade' && (
                <div className="text-2xl">💣</div>
              )}
            </div>
            {animationState.current.attacking && (
              <div className="absolute inset-0 border-2 border-yellow-300 rounded-full animate-ping opacity-75"></div>
            )}
            {animationState.current.shooting && (
              <div className="absolute inset-0 border-2 border-red-400 rounded-full animate-ping opacity-75"></div>
            )}
            {animationState.current.spinning && (
              <div className="absolute inset-0 border-4 border-orange-400 rounded-full animate-ping opacity-75"></div>
            )}
            {animationState.current.lasering && (
              <div className="absolute inset-0 border-2 border-red-500 rounded-full animate-pulse opacity-75"></div>
            )}
          </div>
        </div>
        
        {/* Zombies */}
        {zombies.map(zombie => (
          <div
            key={zombie.id}
            className="absolute w-12 h-12 transition-transform"
            style={{
              left: `${zombie.x - 24}px`,
              top: `${zombie.y - 24}px`,
              transform: (animationState.current.attacking && 
                Math.sqrt(Math.pow(zombie.x - swordsmanPos.current.x, 2) + Math.pow(zombie.y - swordsmanPos.current.y, 2)) < 60) 
                ? 'scale(1.1)' 
                : 'scale(1)'
            }}>
            <div className="relative w-full h-full">
              <div className="absolute inset-0 bg-green-800 rounded-full flex items-center justify-center">
                <Skull className="w-6 h-6 text-gray-200" />
              </div>
              <div className="absolute top-3 left-2 w-2 h-2 bg-red-500 rounded-full"></div>
              <div className="absolute top-3 right-2 w-2 h-2 bg-red-500 rounded-full"></div>
            </div>
          </div>
        ))}
        
        {/* Bullets */}
        {bullets.map(bullet => (
          <div
            key={bullet.id}
            className="absolute w-2 h-2 bg-yellow-400 rounded-full"
            style={{
              left: `${bullet.x - 1}px`,
              top: `${bullet.y - 1}px`,
              transform: `rotate(${bullet.angle}rad)`
            }}
          />
        ))}

        {/* Laser Beam */}
        {isLasering && currentWeapon === 'laser' && (
          <div
            className="absolute bg-red-500 opacity-80 animate-pulse"
            style={{
              left: `${swordsmanPos.current.x}px`,
              top: `${swordsmanPos.current.y}px`,
              width: '300px',
              height: '4px',
              transformOrigin: '0 50%',
              transform: `rotate(${Math.atan2(
                mousePosRef.current.y - swordsmanPos.current.y,
                mousePosRef.current.x - swordsmanPos.current.x
              )}rad)`,
              boxShadow: '0 0 10px #ef4444, 0 0 20px #ef4444, 0 0 30px #ef4444'
            }}
          />
        )}

        {/* Grenades */}
        {grenades.map(grenade => {
          const currentTime = Date.now();
          const elapsed = currentTime - grenade.startTime;
          const progress = Math.min(elapsed / grenade.duration, 1);

          // Calculate arc trajectory
          const currentX = grenade.x + (grenade.targetX - grenade.x) * progress;
          const currentY = grenade.y + (grenade.targetY - grenade.y) * progress - Math.sin(progress * Math.PI) * 50;

          return (
            <div
              key={grenade.id}
              className="absolute w-4 h-4 text-orange-500 flex items-center justify-center animate-bounce"
              style={{
                left: `${currentX - 8}px`,
                top: `${currentY - 8}px`,
                fontSize: '16px'
              }}
            >
              💣
            </div>
          );
        })}
        
        {/* Powerups */}
        {powerUps.map(powerUp => (
          <div
            key={powerUp.id}
            className="absolute w-8 h-8 bg-gray-800 rounded-full border-2 border-white flex items-center justify-center animate-pulse"
            style={{
              left: `${powerUp.x - 16}px`,
              top: `${powerUp.y - 16}px`
            }}>
            <PowerUpIcon type={powerUp.type} />
          </div>
        ))}
        
        {/* Score display */}
        <div className="absolute top-4 left-4 bg-black bg-opacity-70 px-4 py-2 rounded-lg">
          <p className="text-xl font-bold text-yellow-300">SCORE: {score}</p>
          <p className="text-sm text-gray-400">Weapon: {currentWeapon.toUpperCase()}</p>
          {hasGun && !hasAxe && !hasLaser && !hasGrenade && (
            <p className="text-sm text-red-400">GUN UNLOCKED!</p>
          )}
          {hasAxe && !hasLaser && !hasGrenade && (
            <p className="text-sm text-orange-400">AXE UNLOCKED!</p>
          )}
          {hasLaser && !hasGrenade && (
            <p className="text-sm text-red-500">LASER UNLOCKED!</p>
          )}
          {hasGrenade && (
            <p className="text-sm text-orange-500">GRENADE UNLOCKED!</p>
          )}
        </div>
        
        {/* Health display */}
        <div className="absolute top-4 right-4 bg-black bg-opacity-70 px-4 py-2 rounded-lg flex items-center">
          {Array.from({ length: 5 }).map((_, i) => (
            <Heart 
              key={i} 
              className={`w-5 h-5 ${i < health ? 'text-red-500 fill-current' : 'text-gray-500'}`} 
            />
          ))}
        </div>
        
        {/* Active powerups */}
        <PowerUpDisplay
          activePowerUps={activePowerUps}
          powerUpTimeLeft={powerUpTimeLeft}
        />
        
        {/* Next weapon unlock */}
        {!hasGun && score >= 300 && (
          <div className="absolute top-4 right-4 bg-black bg-opacity-70 px-4 py-2 rounded-lg animate-pulse">
            <p className="text-sm text-green-400">Gun at 500 points!</p>
          </div>
        )}
        {hasGun && !hasAxe && score >= 700 && (
          <div className="absolute top-4 right-4 bg-black bg-opacity-70 px-4 py-2 rounded-lg animate-pulse">
            <p className="text-sm text-orange-400">Axe at 1000 points!</p>
          </div>
        )}
        {hasAxe && !hasLaser && score >= 1500 && (
          <div className="absolute top-4 right-4 bg-black bg-opacity-70 px-4 py-2 rounded-lg animate-pulse">
            <p className="text-sm text-red-500">Laser at 2000 points!</p>
          </div>
        )}
        {hasLaser && !hasGrenade && score >= 3500 && (
          <div className="absolute top-4 right-4 bg-black bg-opacity-70 px-4 py-2 rounded-lg animate-pulse">
            <p className="text-sm text-orange-500">Grenade at 5000 points!</p>
          </div>
        )}
        
        {/* Game over screen */}
        {gameOver && (
          <GameOverScreen
            score={score}
            hasGun={hasGun}
            hasAxe={hasAxe}
            hasLaser={hasLaser}
            hasGrenade={hasGrenade}
            onPlayAgain={resetGame}
            onMainMenu={() => setGameStarted(false)}
          />
        )}
      </div>
      
          {/* Instructions */}
          <div className="mt-6 text-center max-w-md">
            <p className="text-gray-400">Movement: WASD | Attack: F Key</p>
            <p className="text-gray-400">Weapon Switch: 1/2/3/4/5 Keys | Aim with mouse</p>
            <p className="text-gray-400">Laser: Hold F to fire | Grenade: F to launch | Kill zombies to get powerups!</p>
          </div>

          {/* PowerUp Glossary */}
          <PowerUpGlossary />
        </>
      )}
    </div>
  );
};

export default ZombieSwordsmanGame;